import { createGlobalStyle } from 'styled-components';
import { resetCss } from './resetCss';
import { BREAKPOINTS, FONT_SIZE, FONT_WEIGHT, media } from './themes/constants';
import {
  lightThemeVariables,
  darkThemeVariables,
  commonThemeVariables,
  antOverrideCssVariables,
  getThemeVariables
} from './themes/themeVariables';
import type { ITheme } from './themes/types';
import type { ThemeType } from '@/interfaces/interfaces';

export default createGlobalStyle<{
  $customTheme?: ITheme;
  $resolvedTheme?: ThemeType;
}>`

  ${resetCss}

  [data-theme='light'],
  :root {
    ${lightThemeVariables}
  }

  [data-theme='dark'] {
    ${darkThemeVariables}
  }

  [data-theme-mode='custom'] {
    ${({ $customTheme, $resolvedTheme }) =>
      $customTheme && $resolvedTheme
        ? getThemeVariables($resolvedTheme, $customTheme)
        : ''}
  }

  :root {
    ${commonThemeVariables};
    ${antOverrideCssVariables};
  } 

  [data-no-transition] * {
    transition: none !important;
  }
  
  .range-picker {
    & .ant-picker-panels {
      @media only screen and ${media.xs} and (max-width: ${BREAKPOINTS.md - 0.02}px) {
        display: flex;
      flex-direction: column;
      }
    }
  }

  .search-dropdown {
    box-shadow: var(--box-shadow);

    @media only screen and ${media.xs} and (max-width: ${BREAKPOINTS.md - 0.02}px)  {
      width: calc(100vw - 16px);
    max-width: 600px;
    }

    @media only screen and ${media.md} {
      max-width: 323px;
    }
  }

  a {
    color: var(--primary-color);
    &:hover,:active {
      color: var(--ant-primary-color-hover);
    }
  }
  
  .d-none {
    display: none;
  }

  .ant-picker-cell {
    color: var(--text-main-color);
  }

  .ant-picker-cell-in-view .ant-picker-calendar-date-value {
    color: var(--text-main-color);
    font-weight: ${FONT_WEIGHT.bold};
  }

  .ant-picker svg {
    color: var(--text-light-color);
  }

  .ant-message {
    @media only screen and ${media.xs} and (max-width: ${BREAKPOINTS.md - 0.02}px) {
      left: 0 !important;
      right: 0 !important;
      transform: none !important;
      padding-inline: 0.5rem;
    }
  }

  .ant-message .ant-message-notice-content {
    max-width: calc(100vw - 1rem);
    white-space: normal;
    overflow-wrap: anywhere;
    text-align: left;
  }

  .notifications-popover {
    max-width: calc(100vw - 1rem);
    z-index: 9999;

    .ant-popover-inner {
      background: var(--background-color);
      box-shadow: var(--box-shadow);
    }

    @media only screen and ${media.xs} and (max-width: ${BREAKPOINTS.md - 0.02}px) {
      left: 0.5rem !important;
      right: 0.5rem !important;
      width: auto;

      .ant-popover-content,
      .ant-popover-inner {
        width: 100%;
      }

      .ant-popover-inner {
        padding: 0.75rem;
      }
    }
  }

  // notifications start
  .ant-notification-notice {
    width: 36rem;
    padding: 2rem;
    min-height: 6rem;

    @media only screen and ${media.xs} and (max-width: ${BREAKPOINTS.md - 0.02}px) {
      width: calc(100vw - 1rem);
      max-width: calc(100vw - 1rem);
      padding: 1rem;
    }
    
    .ant-notification-notice-with-icon .ant-notification-notice-message {
      margin-bottom: 0;
      margin-left: 2.8125rem;
    }

    .ant-notification-notice-with-icon .ant-notification-notice-description {
      margin-left: 4.375rem;
      margin-top: 0;
    }

    .ant-notification-notice-icon {
      font-size: 2.8125rem;
      margin-left: 0
    }

    .ant-notification-notice-close {
      top: 1.25rem;
      right: 1.25rem;
    }

    .ant-notification-notice-close-x {
      display: flex;
      font-size: 0.9375rem;
    }

    .notification-without-description {
      .ant-notification-notice-close {
        top: 1.875rem;
      }
      .ant-notification-notice-with-icon .ant-notification-notice-description  {
        margin-top: 0.625rem;
      }
    }
    
    .title {
      font-size: ${FONT_SIZE.xxl};
      height: 3rem;
      margin-left: 1.5rem;
      display: flex;
      align-items: center;
      font-weight: ${FONT_WEIGHT.bold};

      &.title-only {
        color: var(--text-main-color);
        font-size: ${FONT_SIZE.md};
        height: 2rem;
        line-height: 2rem;
        margin-left: 0.75rem;
        font-weight: ${FONT_WEIGHT.semibold};
      }
  }
  
    .description {
      color: var(--text-main-color);
      font-size: ${FONT_SIZE.md};
      font-weight: ${FONT_WEIGHT.semibold};
      line-height: 1.375rem;
    }
  
    &.ant-notification-notice-success {
      border: 1px solid var(--success-color);
      background: var(--notification-success-color);
      
      .title {
        color: var(--success-color);
      }
    }
  
    &.ant-notification-notice-info {
      border: 1px solid var(--primary-color);
      background: var(--notification-primary-color);
  
      .title {
        color: var(--primary-color);
      }
    }
  
    &.ant-notification-notice-warning {
      border: 1px solid var(--warning-color);
      background: var(--notification-warning-color);
  
      .title {
        color: var(--warning-color);
      }
    }
  
    &.ant-notification-notice-error {
      border: 1px solid var(--error-color);
      background: var(--notification-error-color);
  
      .title {
        color: var(--error-color);
      }
    }
  
    .success-icon {
      color: var(--success-color);
    }
  
    .info-icon {
      color: var(--primary-color);
    }
  
    .warning-icon {
      color: var(--warning-color);
    }
  
    .error-icon {
      color: var(--error-color);
    }
  }
  
  .ant-menu-inline, .ant-menu-vertical {
    border-right: 0;
  }

  .auth-loading-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: grid;
    min-height: 100vh;
    min-height: 100dvh;
    place-items: center;
    background: rgba(15, 23, 42, 0.46);
    -webkit-backdrop-filter: blur(2px);
    backdrop-filter: blur(2px);
  }

  @media only screen and ${media.xs} and (max-width: ${BREAKPOINTS.md - 0.02}px) {
    html {
      -webkit-text-size-adjust: 100%;
      text-size-adjust: 100%;
    }

    .ant-layout-content {
      min-width: 0;
    }

    .ant-card {
      border-radius: 1rem;
    }

    .ant-card-head {
      min-height: 3.5rem;
      padding-inline: 1rem;
    }

    .ant-card-body {
      padding: 1rem;
    }

    .ant-btn:not(.ant-btn-sm),
    .ant-input,
    .ant-select-single .ant-select-selector {
      min-height: 2.75rem;
    }

    .ant-btn.ant-btn-icon-only {
      width: 2.75rem;
      padding-inline: 0;
    }

    .ant-tabs-tab {
      min-height: 2.75rem;
      padding-block: 0.625rem;
    }

    .ant-modal {
      max-width: calc(100vw - 1rem);
      margin: 0.5rem auto;
      padding-bottom: env(safe-area-inset-bottom);
    }

    .ant-drawer-content-wrapper {
      max-width: min(92vw, 25rem);
    }
  }
  // notifications end
`;
